/**
 * Upload-lifecycle state for attachments embedded INLINE in a comment
 * body (card comments + wiki comments). The embed node lives in the
 * editor doc; these rows only track the upload so the composer can
 * collect `attachmentId`s on post, gate the Post button, and clean up on
 * cancel. Pure helpers — no React, so the gating rule is unit-testable.
 */

export type InlineAttachmentStatus = 'uploading' | 'done' | 'failed'

/** Which inline node hosts the attachment — decides the editor helpers used. */
export type InlineAttachmentKind = 'image' | 'file' | 'video'

export interface InlineAttachment {
  localId: string
  kind: InlineAttachmentKind
  /**
   * The editor node's transient placeholder id — the key the composer
   * uses to re-target the SAME node on resolve / retry / dismiss, and to
   * notice when the author deleted the node out from under a failed row.
   */
  placeholderId: string
  /** Populated once the presign + PUT completes. */
  attachmentId: string | null
  status: InlineAttachmentStatus
}

/** Ids of every attachment whose upload landed — what the post carries. */
export function collectReadyAttachmentIds(rows: readonly InlineAttachment[]): string[] {
  const ids: string[] = []
  for (const row of rows) {
    if (row.status === 'done' && row.attachmentId) ids.push(row.attachmentId)
  }
  return ids
}

export function anyAttachmentUploading(rows: readonly InlineAttachment[]): boolean {
  return rows.some((row) => row.status === 'uploading')
}

export function anyAttachmentFailed(rows: readonly InlineAttachment[]): boolean {
  return rows.some((row) => row.status === 'failed')
}

/**
 * Posting is blocked while ANY inline attachment is still in flight OR
 * sits in the failed state. In flight is the obvious half; failed is the
 * one that differs from the wiki doc editor (which gates Save / Publish
 * on in-flight only): a comment posts its body JSON verbatim, so a failed
 * placeholder still in the body would post a broken reference. The author
 * has to Retry or Dismiss it first.
 */
export function hasBlockingAttachment(rows: readonly InlineAttachment[]): boolean {
  return anyAttachmentUploading(rows) || anyAttachmentFailed(rows)
}

export type PostBlockedReason = 'uploading' | 'failed'

/**
 * Which explanation the disabled Post button should carry. Failed wins
 * over uploading: the in-flight one resolves itself, the failed one needs
 * the author to act.
 */
export function postBlockedReason(rows: readonly InlineAttachment[]): PostBlockedReason | null {
  if (anyAttachmentFailed(rows)) return 'failed'
  if (anyAttachmentUploading(rows)) return 'uploading'
  return null
}

/**
 * Failed rows whose placeholder node no longer appears in the serialized
 * body — the author deleted the error chip with Backspace instead of
 * Dismiss. Left in state they would block Post with nothing on screen to
 * act on, so the composer sweeps them. `body` is the editor's JSON
 * value-string; a placeholder id is a session-unique token that appears
 * in it iff the node is still in the doc.
 */
export function findOrphanedFailedRows(
  rows: readonly InlineAttachment[],
  body: string,
): InlineAttachment[] {
  return rows.filter((row) => row.status === 'failed' && !body.includes(row.placeholderId))
}

/**
 * Where each inline embed node keeps its attachment row id. The card
 * `image` and `fileEmbed` nodes use `attachmentId`; the shared
 * `videoEmbed` node (wiki lineage) calls the same id `fileId`.
 */
const EMBED_ID_ATTR: Readonly<Record<string, string>> = {
  image: 'attachmentId',
  fileEmbed: 'attachmentId',
  videoEmbed: 'fileId',
}

interface LooseEmbedNode {
  type?: unknown
  attrs?: unknown
  content?: unknown
}

/** Depth cap mirroring the BE rich-text validator's guard. */
const MAX_WALK_DEPTH = 128

/**
 * Every attachment id an inline embed node in `doc` still points at.
 * `doc` is the TipTap JSON about to be posted; anything else (a legacy
 * markdown string, null) references nothing structurally.
 */
export function collectReferencedAttachmentIds(doc: unknown): Set<string> {
  const ids = new Set<string>()
  const walk = (node: unknown, depth: number): void => {
    if (depth > MAX_WALK_DEPTH || node === null || typeof node !== 'object') return
    const n = node as LooseEmbedNode
    const attr = typeof n.type === 'string' ? EMBED_ID_ATTR[n.type] : undefined
    if (attr && n.attrs !== null && typeof n.attrs === 'object') {
      const id = (n.attrs as Record<string, unknown>)[attr]
      if (typeof id === 'string' && id.length > 0) ids.add(id)
    }
    if (Array.isArray(n.content)) {
      for (const child of n.content) walk(child, depth + 1)
    }
  }
  walk(doc, 0)
  return ids
}

/**
 * The subset of `readyIds` the posted body still references, in their
 * original order. A finished upload whose embed the author Backspaced out
 * keeps its row (deleting the bytes on Backspace would break Cmd+Z
 * restoring the node), so the post must not offer it for binding — the
 * server frees what is left unbound. A string body (pre-JSON format) is
 * matched textually, which is safe because the ids are UUIDs.
 */
export function filterReferencedAttachmentIds(
  readyIds: readonly string[],
  body: unknown,
): string[] {
  if (typeof body === 'string') return readyIds.filter((id) => body.includes(id))
  const referenced = collectReferencedAttachmentIds(body)
  return readyIds.filter((id) => referenced.has(id))
}
